import type { ContractDocument, ContractVersionView } from "@closer/shared";
import { Button, FormField, Input, Modal, Select, Skeleton, Textarea } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

export type OneriGirdisi = {
  sourceVersionId: string;
  targetClauseId: string;
  targetBlockIndex: number | null;
  currentText: string;
  proposedText: string;
  note?: string;
};

/** Hedef seçeneği: madde başlığı ya da bir blok. Değer `<maddeId>#<blokIndeksi|baslik>`. */
type Hedef = { deger: string; etiket: string; metin: string };

function hedefler(document: ContractDocument | null): Hedef[] {
  if (!document) return [];
  return document.clauses.flatMap((madde) => [
    {
      deger: `${madde.id}#baslik`,
      etiket: `${madde.title || "(başlıksız madde)"} — başlık`,
      metin: madde.title,
    },
    ...madde.blocks.map((blok, i) => ({
      deger: `${madde.id}#${i}`,
      etiket: `${madde.title || "(başlıksız madde)"} — ${i + 1}. paragraf`,
      metin: blok.text,
    })),
  ]);
}

/**
 * REVİZYON ÖNERİSİ OLUŞTURMA (CLOS-138 `[093]`).
 *
 * ⚠ HEDEF SEÇİLİYOR, mevcut metin ondan DOĞUYOR. Mevcut metni kullanıcıya yazdırsaydık,
 * ekranında görmediği bir metne öneri yapabilir ve sunucunun karşılaştırması yanlış hedefi
 * doğrulardı — kart bu yüzden `currentText`'i zorunlu tutuyor.
 *
 * ⚠ Kaynak versiyon SEÇİLİYOR: öneri bir metnin belirli bir hâline yapılır. "En sonuncusu"
 * diye sabitleseydik, kullanıcı elindeki eski çıktı üzerinden konuşurken öneri başka bir
 * metne bağlanırdı.
 *
 * ⚠ Yalnız uygulama içi metin taşıyan versiyonlar hedeflenebiliyor: dosya kaynaklı bir
 * versiyonun maddesi yok, madde bazlı öneri de yapılamaz.
 */
export function RevisionProposalDialog({
  open,
  onClose,
  contractId,
  versiyonlar,
  onConfirm,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  contractId: string;
  versiyonlar: ContractVersionView[];
  onConfirm: (girdi: OneriGirdisi) => void;
  isPending: boolean;
}) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const [versiyon, setVersiyon] = useState("");
  const [hedef, setHedef] = useState("");
  const [oneri, setOneri] = useState("");
  const [not, setNot] = useState("");

  const belge = useQuery({
    enabled: open && versiyon !== "",
    queryKey: tenantKey(tenantId, "contract", contractId, "versiyon-belgesi", versiyon),
    queryFn: async () =>
      (
        await api<{ document: ContractDocument | null }>(
          `/api/contracts/${contractId}/versions/${versiyon}/document`,
          { tenantId },
        )
      ).data,
  });

  const secenekler = hedefler(belge.data?.document ?? null);
  const secili = secenekler.find((h) => h.deger === hedef) ?? null;

  useEffect(() => {
    if (!open) return;
    setVersiyon("");
    setHedef("");
    setOneri("");
    setNot("");
  }, [open]);

  /** Versiyon değişince hedef geçersizleşir: eski maddeye ait seçim yeni belgede yok. */
  useEffect(() => {
    setHedef("");
  }, [versiyon]);

  function gonder() {
    if (!secili) return;
    const [maddeId = "", parca = ""] = secili.deger.split("#");
    const govde: OneriGirdisi = {
      sourceVersionId: versiyon,
      targetClauseId: maddeId,
      /** `null` "başlığı hedefliyorum" demek; şema bunu böyle okuyor. */
      targetBlockIndex: parca === "baslik" ? null : Number(parca),
      currentText: secili.metin,
      proposedText: oneri,
    };
    const notMetni = not.trim();
    if (notMetni.length > 0) govde.note = notMetni;
    onConfirm(govde);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Revizyon önerisi"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            onClick={gonder}
            loading={isPending}
            disabled={secili === null || oneri.trim() === ""}
          >
            Gönder
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FormField label="Kaynak versiyon" required>
          <Select
            aria-label="Kaynak versiyon"
            value={versiyon}
            onChange={(e) => setVersiyon(e.target.value)}
          >
            <option value="">Seçin</option>
            {versiyonlar.map((v) => (
              <option key={v.id} value={v.id}>
                {`Versiyon ${v.versionNumber}`}
              </option>
            ))}
          </Select>
        </FormField>

        {versiyon === "" ? null : belge.isPending ? (
          <div role="status" aria-label="Belge yükleniyor">
            <Skeleton lines={2} />
          </div>
        ) : belge.isError ? (
          /*
            ⚠ ARIZA, ÜRÜN KURALI DEĞİL (CLOS-832).

            Bu dal eskiden YOKTU: sorgu düştüğünde `belge.data` boş kalıyor, `secenekler`
            de boş çıkıyor ve kullanıcıya "bu versiyon uygulama içi metin taşımıyor"
            deniyordu. Yani bir ARIZA, ürünün kuralıymış gibi anlatılıyordu — kullanıcı
            yeniden denemek yerine versiyonun yapısını sorgulardı ve destek de yanlış yere
            bakardı.

            İki dal birbirinin yerine GEÇEMEZ: burada sebep bilinmiyor ve tekrar denemek
            anlamlı; aşağıda sebep biliniyor ve tekrar denemek anlamsız.
          */
          <StateView
            state={resolveErrorState(belge.error, { idempotent: true })}
            onRetry={() => void belge.refetch()}
          />
        ) : secenekler.length === 0 ? (
          /* Dosya kaynaklı versiyonun maddesi yok; sessiz boş liste yerine sebebi söylüyoruz. */
          <p className="text-sm text-warning">
            Bu versiyon uygulama içi metin taşımıyor; madde bazlı öneri yapılamaz.
          </p>
        ) : (
          <>
            <FormField label="Hedef" required>
              <Select aria-label="Hedef" value={hedef} onChange={(e) => setHedef(e.target.value)}>
                <option value="">Seçin</option>
                {secenekler.map((h) => (
                  <option key={h.deger} value={h.deger}>
                    {h.etiket}
                  </option>
                ))}
              </Select>
            </FormField>

            {secili ? (
              <FormField label="Mevcut metin">
                {/* Salt okunur: metin belgeden geliyor, kullanıcı değiştiremez. */}
                <Input aria-label="Mevcut metin" value={secili.metin} readOnly />
              </FormField>
            ) : null}

            <FormField label="Önerilen metin" required>
              <Textarea
                aria-label="Önerilen metin"
                rows={4}
                value={oneri}
                onChange={(e) => setOneri(e.target.value)}
              />
            </FormField>

            <FormField label="Not">
              <Textarea
                aria-label="Not"
                rows={2}
                value={not}
                onChange={(e) => setNot(e.target.value)}
              />
            </FormField>
          </>
        )}
      </div>
    </Modal>
  );
}
